// src/Services/UserService.js
import { ServiceProvider } from "./ServiceProvider";
import apiCall from "../utils/apiInterceptor";

class UserService {
    static async getAll(page = 1, limit = 10, search = "") {
        try {
            const baseUrl = search
                ? `${ServiceProvider.user.filter}?search=${encodeURIComponent(search)}`
                : `${ServiceProvider.user.getAll}?page_no=${page}&count=${limit}`;

            const response = await apiCall(baseUrl, {
                method: "GET",
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw {
                    message: errorData?.message || "Failed to fetch users",
                    status: response.status,
                };
            }

            const result = await response.json();

            return {
                users: result?.data?.data || result?.data || [],
                total: result?.data?.count ?? (Array.isArray(result?.data?.data) ? result.data.data.length : 0),
                status: result?.status,
            };
        } catch (error) {
            console.error("Error fetching users:", error);
            if (error.message && error.status) {
                throw error;
            }
            throw {
                message: error.message || "Unable to process your request",
                status: 500
            };
        }
    }

    static async create(userData) {
        try {
            const response = await apiCall(ServiceProvider.user.create, {
                method: "POST",
                body: JSON.stringify(userData),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw errorData;
            }

            return await response.json();
        } catch (error) {
            console.error("Error creating user:", error);
            if (error.message && error.status) {
                throw error;
            }
            throw {
                message: error.message || "Unable to process your request",
                status: 500,
            };
        }
    }

    static async update(userId, updateData) {
        try {
            const response = await apiCall(`${ServiceProvider.user.update}${userId}`, {
                method: "POST",
                body: JSON.stringify(updateData),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw {
                    message: errorData?.message || "Failed to update user",
                    status: response.status,
                };
            }

            return await response.json();
        } catch (error) {
            console.error("Error updating user:", error);
            if (error.message && error.status) {
                throw error;
            }
            throw {
                message: error.message || "Unable to process your request",
                status: 500,
            };
        }
    }

    static async delete(userId) {
        try {
            const response = await apiCall(`${ServiceProvider.user.delete}${userId}`, {
                method: "DELETE",
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw {
                    message: errorData?.message || "Failed to delete user",
                    status: response.status,
                };
            }

            return await response.json();
        } catch (error) {
            console.error("Error deleting user:", error);
            if (error.message && error.status) {
                throw error;
            }
            throw {
                message: error.message || "Unable to process your request",
                status: 500,
            };
        }
    }

    static async getByUID(uid) {
        try {
            const response = await apiCall(`${ServiceProvider.user.getByUID}${uid}`, {
                method: "GET",
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw {
                    message: errorData?.message || "Failed to fetch user details",
                    status: response.status,
                };
            }

            return await response.json();
        } catch (error) {
            console.error("Error fetching user by UID:", error);
            if (error.message && error.status) {
                throw error;
            }
            throw {
                message: error.message || "Unable to process your request",
                status: 500,
            };
        }
    }

    static async getBranch() {
        try {
            const response = await apiCall(ServiceProvider.user.getBranch, {
                method: "GET",
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw {
                    message: errorData?.message || "Failed to fetch branch details",
                    status: response.status,
                };
            }

            return await response.json();
        } catch (error) {
            console.error("Error fetching branch:", error);
            if (error.message && error.status) {
                throw error;
            }
            throw {
                message: error.message || "Unable to process your request",
                status: 500,
            };
        }
    }

    static async getByFilter(filterQuery) {
        try {
            const response = await apiCall(`${ServiceProvider.user.getByFilter}${filterQuery}`, {
                method: "GET",
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw {
                    message: errorData?.message || "Failed to fetch user by filter",
                    status: response.status,
                };
            }

            return await response.json();
        } catch (error) {
            console.error("Error fetching user by filter:", error);
            if (error.message && error.status) {
                throw error;
            }
            throw {
                message: error.message || "Unable to process your request",
                status: 500,
            };
        }
    }

    static async changePassword(data) {
        try {
            const response = await apiCall(ServiceProvider.user.changePassword, {
                method: "POST",
                body: JSON.stringify(data),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw {
                    message: errorData?.message || "Failed to change password",
                    status: response.status,
                };
            }

            return await response.json();
        } catch (error) {
            console.error("Error changing password:", error);
            if (error.message && error.status) {
                throw error;
            }
            throw {
                message: error.message || "Unable to process your request",
                status: 500,
            };
        }
    }
}

export default UserService;